import { useLocaleState } from "ra-core";
import { useMemo } from "react";

import { TIME_FORMAT, toIntlLocale } from "@/lib/dates";
import { formatCompactMoney, formatMoney } from "@/lib/money";

import { useConfigurationContext } from "../../root/ConfigurationContext";

/** Locale-aware formatters of the home: whole-unit money, dates, times. */
export const useHomeFormat = () => {
  const [locale = "es"] = useLocaleState();
  const { currency } = useConfigurationContext();
  return useMemo(() => {
    const numberFormat = new Intl.NumberFormat(locale, {
      maximumFractionDigits: 1,
    });
    // Dates follow the app locale (en -> en-GB: day first, 24-hour clock).
    const dateLocale = toIntlLocale(locale);
    const timeFormat = new Intl.DateTimeFormat(dateLocale, TIME_FORMAT);
    const shortDate = new Intl.DateTimeFormat(dateLocale, {
      day: "numeric",
      month: "short",
    });
    const longDate = new Intl.DateTimeFormat(dateLocale, {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
    const monthYear = new Intl.DateTimeFormat(dateLocale, {
      month: "long",
      year: "numeric",
    });
    const monthShort = new Intl.DateTimeFormat(dateLocale, { month: "short" });
    const monthLong = new Intl.DateTimeFormat(dateLocale, { month: "long" });
    const capitalize = (value: string) =>
      value.charAt(0).toUpperCase() + value.slice(1);
    return {
      locale,
      // Whole units on the home: KPIs are a glance, not an invoice.
      money: (value: number) =>
        formatMoney(Math.round(value), locale, currency),
      compactMoney: (value: number) =>
        formatCompactMoney(value, locale, currency),
      number: (value: number) => numberFormat.format(value),
      time: (date: Date) => timeFormat.format(date),
      shortDate: (date: Date) => shortDate.format(date),
      longDate: (date: Date) => capitalize(longDate.format(date)),
      monthYear: (date: Date) => capitalize(monthYear.format(date)),
      monthShort: (date: Date) => monthShort.format(date).replace(".", ""),
      monthName: (date: Date) => monthLong.format(date),
      percent: (ratio: number) =>
        new Intl.NumberFormat(locale, {
          style: "percent",
          maximumFractionDigits: 0,
        }).format(ratio),
    };
  }, [locale, currency]);
};

export type HomeFormat = ReturnType<typeof useHomeFormat>;
